import { useMemo } from 'react';

import { useTemplateContextFieldsQuery } from '@/services/api/templatesV2ServiceEnhanced';

interface HtmlPreviewProps {
  body: string;
  customValues?: Record<string, string>;
}

/**
 * Renders a Jinja2 HTML template body with sample values substituted,
 * displayed in a sandboxed iframe.
 */
export const HtmlPreview = ({ body, customValues }: HtmlPreviewProps) => {
  const { data: contextFields } = useTemplateContextFieldsQuery();

  const sampleValues = useMemo(() => {
    if (!contextFields) return {} as Record<string, string>;
    const map: Record<string, string> = {};
    contextFields.categories.forEach((cat) => {
      cat.fields.forEach((f) => {
        map[f.key] = f.sample;
      });
    });
    return map;
  }, [contextFields]);

  const mergedValues = useMemo(() => {
    if (!customValues) return sampleValues;
    return { ...sampleValues, ...customValues };
  }, [sampleValues, customValues]);

  const renderedHtml = useMemo(() => {
    if (!body) return '';
    return body.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, varName: string) => {
      return mergedValues[varName] ?? match;
    });
  }, [body, mergedValues]);

  if (!body) {
    return (
      <div className="flex h-full flex-col">
        <div className="px-3 py-2">
          <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
            Preview
          </span>
        </div>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-sm font-medium text-zinc-500">
            Enter content above to see preview
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
          Preview
        </span>
      </div>
      <iframe
        srcDoc={renderedHtml}
        title="HTML template preview"
        className="h-full w-full border-0"
        sandbox=""
      />
    </div>
  );
};
